import React, { useCallback, useEffect, useRef, useState } from "react";
import { CircleAlert, HelpCircle } from "lucide-react";
import { useTranslation } from "react-i18next";
import { DetectionOverlay } from "./DetectionOverlay";
import { DetectionStatus } from "./DetectionStatus";
import { DocumentDetectionDebug } from "./DocumentDetectionDebug";
import { getDocumentDetectionClient } from "./DocumentDetectionClient";
import {
  DOCUMENT_CONFIG,
  getDetectorInterval,
} from "./documentDetectionConfig";
import { createEmptyCompliance } from "./DocumentGeometry";
import { DocumentTracker } from "./DocumentTracker";
import {
  cropDocumentPreview,
  cropDocumentPreviewToGuide,
} from "./DocumentPreviewCropper";
import { extractLastFrame } from "./LastFrameExtractor";
import { captureVideoFrame } from "./FrameCapture";
import {
  getManualCaptureCountdown,
  MANUAL_CAPTURE_DURATION_MS,
} from "./ManualCaptureTiming";
import {
  createRecordedVideoFile,
  RecordingController,
} from "./RecordingController";
import { logDocumentCamera, warnDocumentCamera } from "./documentCameraDebug";
import {
  DOCUMENT_TYPES,
  normalizeDocumentType,
} from "../services/documentRequirements";

/**
 * 证件拍摄模块总控制器。
 *
 * 主流程顺序：申请摄像头 -> 初始化检测器 -> 逐帧追踪四角 -> 判断合规/稳定
 * -> 提前录制 -> 自动或手动触发拍照 -> 生成视频和预览 -> 回调上层 KYC 流程。
 * 详细的跨文件调用关系见同目录 README.md。
 */
const CAPTURE_FLASH_MS = 180;
const MINIMUM_RECORDING_MS = 3000;
const MAXIMUM_RECORDING_MS = 8000;
const DETECTION_LOG_INTERVAL_MS = 1500;

// cameraStatus 是资源/拍摄状态；检测提示另由 detectionStatus 管理。
const STATUS = Object.freeze({
  INITIAL: "initial",
  REQUESTING: "requesting",
  SCANNING: "scanning",
  DOCUMENT_INVALID: "document_invalid",
  DOCUMENT_READY: "document_ready",
  RECORDING: "recording",
  PROCESSING: "processing",
  ERROR: "error",
});

const LIVE_CAMERA_STATUSES = new Set([
  STATUS.SCANNING,
  STATUS.DOCUMENT_INVALID,
  STATUS.DOCUMENT_READY,
  STATUS.RECORDING,
  STATUS.PROCESSING,
]);

const wait = (duration) =>
  new Promise((resolve) => globalThis.setTimeout(resolve, duration));

const roundMetric = (value) =>
  Number.isFinite(value) ? Number(value.toFixed(3)) : 0;

const getRecordingElapsed = (startedAt) =>
  startedAt ? Math.max(0, Date.now() - startedAt) : 0;

const createInitialDetection = (frameAspectRatio = 4 / 3) => ({
  ...createEmptyCompliance(frameAspectRatio),
  fresh: false,
  trackingHeld: false,
  stable: false,
  stableFrames: 0,
  stableElapsedMs: 0,
  stableProgress: 0,
  missedFrames: 0,
  detectorFps: 0,
  status: "SEARCHING",
});

const isMobileDevice = () =>
  Boolean(
    navigator.userAgentData?.mobile ||
    globalThis.matchMedia?.("(pointer: coarse)").matches,
  );

export function DocumentCamera({
  side = "front",
  documentType,
  autoStart = true,
  onCapture,
}) {
  const { t } = useTranslation();

  // refs 保存高频变化或浏览器资源，避免每个视频帧都触发 React 渲染。
  const videoRef = useRef(null);
  const detectionCanvasRef = useRef(null);
  const previewCanvasRef = useRef(null);
  const streamRef = useRef(null);
  const recorderControllerRef = useRef(null);
  const animationFrameRef = useRef(null);
  const lastDetectionAtRef = useRef(0);
  const detectionBusyRef = useRef(false);
  const detectionErrorFramesRef = useRef(0);
  const trackerRef = useRef(new DocumentTracker());
  const latestDetectionRef = useRef(createInitialDetection());
  const detectionIntervalRef = useRef(getDetectorInterval(isMobileDevice()));
  const previewFrameBlobRef = useRef(null);
  const recordingStartedAtRef = useRef(0);
  const modeRef = useRef("automatic");
  const statusRef = useRef(STATUS.INITIAL);
  const mountedRef = useRef(true);
  const videoMetadataLoggedRef = useRef(false);
  const detectionLogRef = useRef({
    frames: 0,
    lastStatus: "",
    lastLoggedAt: 0,
  });

  // state 只保存需要驱动界面更新的状态。
  const [mode, setMode] = useState("automatic");
  const [cameraStatus, setCameraStatus] = useState(STATUS.INITIAL);
  const [isDetecting, setIsDetecting] = useState(false);
  const [detection, setDetection] = useState(createInitialDetection);
  const [detectionStatus, setDetectionStatus] = useState("SEARCHING");
  const [manualCountdown, setManualCountdown] = useState(0);
  const [manualProgress, setManualProgress] = useState(0);
  const [showHelp, setShowHelp] = useState(false);
  const [showFlash, setShowFlash] = useState(false);
  const [error, setError] = useState("");
  const isBack = side === "back";
  const isPassport =
    normalizeDocumentType(documentType) === DOCUMENT_TYPES.PASSPORT;
  const cameraIsLive = LIVE_CAMERA_STATUSES.has(cameraStatus);
  const controlsLocked = [
    STATUS.INITIAL,
    STATUS.REQUESTING,
    STATUS.RECORDING,
    STATUS.PROCESSING,
  ].includes(cameraStatus);

  // 同步 ref 与 state，异步回调读取 ref 可避免闭包拿到旧状态。
  const transitionTo = useCallback(
    (nextStatus) => {
      if (statusRef.current !== nextStatus) {
        logDocumentCamera("Camera state changed", {
          side,
          from: statusRef.current,
          to: nextStatus,
        });
      }
      statusRef.current = nextStatus;
      if (mountedRef.current) setCameraStatus(nextStatus);
    },
    [side],
  );

  const stopCamera = useCallback(() => {
    if (streamRef.current) {
      logDocumentCamera("Camera stream stopped", { side });
    }
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;

    if (videoRef.current) videoRef.current.srcObject = null;
  }, [side]);

  // 切换模式或重新打开相机时，清空上一轮四角、稳定计时和调试统计。
  const resetDetection = useCallback(() => {
    const videoAspectRatio = videoRef.current?.videoWidth
      ? videoRef.current.videoWidth / Math.max(videoRef.current.videoHeight, 1)
      : 4 / 3;
    trackerRef.current.reset();
    lastDetectionAtRef.current = 0;
    detectionBusyRef.current = false;
    detectionErrorFramesRef.current = 0;
    videoMetadataLoggedRef.current = false;
    detectionLogRef.current = {
      frames: 0,
      lastStatus: "",
      lastLoggedAt: 0,
    };
    const initialDetection = createInitialDetection(videoAspectRatio);
    latestDetectionRef.current = initialDetection;
    setDetection(initialDetection);
    setDetectionStatus("SEARCHING");
  }, []);

  const handleCaptureError = useCallback(
    (messageKey = "kyc.camera.recordingFailed") => {
      warnDocumentCamera(
        "Capture pipeline stopped",
        new Error(`${messageKey} (${side})`),
      );
      setIsDetecting(false);
      setManualCountdown(0);
      recorderControllerRef.current?.cancel();
      recorderControllerRef.current = null;
      stopCamera();
      transitionTo(STATUS.ERROR);
      if (mountedRef.current) setError(t(messageKey));
    },
    [side, stopCamera, t, transitionTo],
  );

  // 检测器失败不应该阻塞 KYC：保留摄像头并降级到可操作的手动模式。
  const handleDetectionUnavailable = useCallback(
    (detectionError) => {
      warnDocumentCamera("Document detector unavailable", detectionError);
      setIsDetecting(false);
      recorderControllerRef.current?.cancel();
      recorderControllerRef.current = null;
      recordingStartedAtRef.current = 0;
      modeRef.current = "manual";
      setMode("manual");
      resetDetection();
      setDetectionStatus("MANUAL");
      transitionTo(STATUS.SCANNING);
      if (mountedRef.current) {
        setError(t("kyc.camera.detectionUnavailable"));
      }
    },
    [resetDetection, t, transitionTo],
  );

  // MediaRecorder 停止后的统一出口：准备预览、封装 File，并把结果交给上层步骤。
  const completeVideoCapture = useCallback(
    async (videoBlob, existingFile, capturedPreviewBlob = null) => {
      logDocumentCamera("Preparing captured document preview", {
        side,
        videoBytes: videoBlob?.size || 0,
        hasCapturedFrame: Boolean(capturedPreviewBlob),
      });
      transitionTo(STATUS.PROCESSING);
      setDetectionStatus("PROCESSING");

      try {
        const previewBlob =
          capturedPreviewBlob ||
          (await extractLastFrame(videoBlob, {
            canvas: previewCanvasRef.current,
            fallbackVideo: videoRef.current,
          }));
        const previewUrl = URL.createObjectURL(previewBlob);
        const recordedVideoFile =
          existingFile || createRecordedVideoFile(videoBlob, side);
        const recordedDurationMs = recordingStartedAtRef.current
          ? Math.min(
              Date.now() - recordingStartedAtRef.current,
              MAXIMUM_RECORDING_MS,
            )
          : 0;

        if (!mountedRef.current) {
          URL.revokeObjectURL(previewUrl);
          return;
        }

        stopCamera();
        logDocumentCamera("Document capture completed", {
          side,
          durationMs: recordedDurationMs,
          mimeType: recordedVideoFile.type || videoBlob.type,
          videoBytes: videoBlob?.size || 0,
          previewBytes: previewBlob?.size || 0,
        });
        onCapture({
          file: recordedVideoFile,
          recordedVideoBlob: videoBlob,
          previewImage: previewUrl,
          previewUrl,
          mediaType: "video",
          mimeType: recordedVideoFile.type || videoBlob.type,
          durationMs: recordedDurationMs,
          isPlaceholder: false,
        });
      } catch (previewError) {
        warnDocumentCamera("Preview extraction failed", previewError);
        handleCaptureError("kyc.camera.previewFailed");
      } finally {
        previewFrameBlobRef.current = null;
        recordingStartedAtRef.current = 0;
        if (mountedRef.current) {
          setManualCountdown(0);
          setShowFlash(false);
        }
      }
    },
    [handleCaptureError, onCapture, side, stopCamera, transitionTo],
  );

  // 创建不自动停止的滚动录像；RecordingController 内部只保留最近 8 秒分片。
  const startBackgroundRecording = useCallback(
    (stream, trigger) => {
      if (recorderControllerRef.current?.isRecording()) return true;

      const controller = new RecordingController({
        stream,
        autoStop: false,
        bufferWindowMs: MAXIMUM_RECORDING_MS,
        onComplete: (videoBlob) => {
          recorderControllerRef.current = null;
          completeVideoCapture(videoBlob, null, previewFrameBlobRef.current);
        },
        onError: () => handleCaptureError("kyc.camera.recordingFailed"),
      });

      recorderControllerRef.current = controller;
      recordingStartedAtRef.current = Date.now();
      if (controller.start()) {
        logDocumentCamera("Background capture started", {
          side,
          trigger,
          bufferWindowMs: MAXIMUM_RECORDING_MS,
        });
        return true;
      }

      warnDocumentCamera(
        "Background capture did not start",
        new Error("MediaRecorder could not enter the recording state."),
      );
      recorderControllerRef.current = null;
      recordingStartedAtRef.current = 0;
      return false;
    },
    [completeVideoCapture, handleCaptureError],
  );

  // 手动模式不运行证件检测。点击快门后才开始固定 3 秒录像，结束时从录像末帧生成预览。
  const startManualCapture = useCallback(() => {
    if (
      modeRef.current !== "manual" ||
      [STATUS.RECORDING, STATUS.PROCESSING, STATUS.ERROR].includes(
        statusRef.current,
      )
    ) {
      return;
    }

    const stream = streamRef.current;
    const streamIsLive = stream
      ?.getVideoTracks()
      .some((track) => track.readyState === "live");
    if (!streamIsLive) {
      handleCaptureError("kyc.camera.unavailable");
      return;
    }

    setError("");
    setIsDetecting(false);
    setManualCountdown(getManualCaptureCountdown(MANUAL_CAPTURE_DURATION_MS));
    setManualProgress(0);
    setShowHelp(false);
    previewFrameBlobRef.current = null;

    let finalFramePromise = null;
    const controller = new RecordingController({
      stream,
      durationMs: MANUAL_CAPTURE_DURATION_MS,
      autoStop: true,
      onProgress: ({ remainingMs, progress }) => {
        if (mountedRef.current) {
          setManualCountdown(getManualCaptureCountdown(remainingMs));
          setManualProgress(Math.min(Math.max(Number(progress) || 0, 0), 1));
        }
      },
      onStopping: () => {
        finalFramePromise = captureVideoFrame(
          videoRef.current,
          previewCanvasRef.current,
        )
          .then(async (fullFrameBlob) => {
            try {
              const croppedPreview = await cropDocumentPreviewToGuide(
                previewCanvasRef.current,
              );
              logDocumentCamera("Manual guide crop completed", {
                side,
                outputWidth: croppedPreview.width,
                outputHeight: croppedPreview.height,
                previewBytes: croppedPreview.blob.size,
              });
              return croppedPreview.blob;
            } catch (cropError) {
              warnDocumentCamera("Manual guide crop failed", cropError);
              return fullFrameBlob;
            }
          })
          .catch((frameError) => {
            warnDocumentCamera("Final live frame capture failed", frameError);
            return null;
          });

        transitionTo(STATUS.PROCESSING);
        setDetectionStatus("PROCESSING");
        if (mountedRef.current) {
          setManualCountdown(0);
          setManualProgress(1);
          setShowFlash(true);
        }
      },
      onComplete: async (videoBlob) => {
        recorderControllerRef.current = null;
        const finalFrameBlob = await finalFramePromise;
        completeVideoCapture(videoBlob, null, finalFrameBlob);
      },
      onError: () => handleCaptureError("kyc.camera.recordingFailed"),
    });

    recorderControllerRef.current = controller;
    recordingStartedAtRef.current = Date.now();
    if (!controller.start()) {
      recorderControllerRef.current = null;
      recordingStartedAtRef.current = 0;
      return;
    }

    transitionTo(STATUS.RECORDING);
    setDetectionStatus("RECORDING");
    logDocumentCamera("Manual three-second capture started", {
      side,
      durationMs: MANUAL_CAPTURE_DURATION_MS,
    });
  }, [completeVideoCapture, handleCaptureError, side, transitionTo]);

  // 自动模式保留检测、预录、触发帧和透视裁切逻辑。
  const captureAutomaticDocument = useCallback(async () => {
    if (
      modeRef.current !== "automatic" ||
      [STATUS.RECORDING, STATUS.PROCESSING, STATUS.ERROR].includes(
        statusRef.current,
      )
    ) {
      return;
    }

    logDocumentCamera("Capture triggered", {
      side,
      mode: "automatic",
      trigger: "stable-document",
    });
    setIsDetecting(false);
    transitionTo(STATUS.PROCESSING);
    setDetectionStatus("PROCESSING");

    try {
      if (!recorderControllerRef.current?.isRecording()) {
        if (!startBackgroundRecording(streamRef.current, "automatic-capture")) {
          throw new Error("The background capture could not be started.");
        }
      }

      const capturedFrameBlob = await captureVideoFrame(
        videoRef.current,
        previewCanvasRef.current,
      );
      previewFrameBlobRef.current = capturedFrameBlob;

      const capturedDetection = latestDetectionRef.current;
      if (
        capturedDetection?.detected &&
        capturedDetection?.corners?.length === 4
      ) {
        try {
          logDocumentCamera("Document perspective crop started", {
            side,
            sourceWidth: previewCanvasRef.current?.width,
            sourceHeight: previewCanvasRef.current?.height,
            normalizedCorners: capturedDetection.corners,
          });
          const croppedPreview = await cropDocumentPreview(
            previewCanvasRef.current,
            capturedDetection.corners,
          );
          previewFrameBlobRef.current = croppedPreview.blob;
          logDocumentCamera("Document perspective crop completed", {
            side,
            outputWidth: croppedPreview.width,
            outputHeight: croppedPreview.height,
            previewBytes: croppedPreview.blob.size,
          });
        } catch (cropError) {
          warnDocumentCamera("Document perspective crop failed", cropError);
        }
      }

      setShowFlash(true);
      await wait(CAPTURE_FLASH_MS);
      if (mountedRef.current) setShowFlash(false);

      const remainingRecordingMs = Math.max(
        0,
        MINIMUM_RECORDING_MS -
          getRecordingElapsed(recordingStartedAtRef.current),
      );
      if (remainingRecordingMs > 0) {
        logDocumentCamera("Waiting for minimum recording duration", {
          side,
          remainingMs: remainingRecordingMs,
          minimumRecordingMs: MINIMUM_RECORDING_MS,
        });
        await wait(remainingRecordingMs);
      }

      if (!recorderControllerRef.current?.stop()) {
        throw new Error("The background capture could not be completed.");
      }
    } catch (captureError) {
      warnDocumentCamera("Frame capture failed", captureError);
      handleCaptureError("kyc.camera.captureFailed");
    }
  }, [handleCaptureError, side, startBackgroundRecording, transitionTo]);

  // 组件卸载时释放本组件持有的动画帧、录像和摄像头；共享检测 Worker 保留供后续页面复用。
  useEffect(() => {
    mountedRef.current = true;
    logDocumentCamera("Component mounted", { side, autoStart });

    return () => {
      logDocumentCamera("Component unmounted", { side });
      mountedRef.current = false;
      globalThis.cancelAnimationFrame(animationFrameRef.current);
      recorderControllerRef.current?.cancel();
      recorderControllerRef.current = null;
      stopCamera();
    };
  }, [autoStart, side, stopCamera]);

  /**
   * 检测主链路：
   * DetectionClient 输出像素四角 -> DocumentTracker 归一化并跨帧平滑
   * -> DocumentGeometry 计算严格/容错合规结果 -> React 状态驱动四角绘制和录像。
   */
  useEffect(() => {
    if (!isDetecting) return undefined;
    let stopped = false;
    const detector = getDocumentDetectionClient();
    const detectionInterval = detectionIntervalRef.current;
    logDocumentCamera("Document frame inspection started", {
      side,
      intervalMs: detectionInterval,
      sampleWidth: DOCUMENT_CONFIG.detectorSampleWidth,
      stableDurationMs: DOCUMENT_CONFIG.stableDuration,
      targetDetectorFps: Math.round(1000 / detectionInterval),
    });

    // 将 Worker 的单帧结果转换为业务决策：更新 UI、启动录像或触发自动拍照。
    const applyDetection = (workerResult, timestamp) => {
      const automaticMode = modeRef.current === "automatic";
      if (!automaticMode) return;

      const nextDetection = {
        ...trackerRef.current.update({
          detectorOutput: workerResult.detectorOutput,
          frameWidth: workerResult.frameWidth,
          frameHeight: workerResult.frameHeight,
          timestamp,
        }),
        detectorTimeMs: workerResult.detectorTimeMs,
      };
      latestDetectionRef.current = nextDetection;
      const debugState = detectionLogRef.current;
      debugState.frames += 1;
      if (
        debugState.lastStatus !== nextDetection.status ||
        timestamp - debugState.lastLoggedAt >= DETECTION_LOG_INTERVAL_MS
      ) {
        logDocumentCamera("Detection result", {
          side,
          frame: debugState.frames,
          status: nextDetection.status,
          detected: nextDetection.detected,
          corners: nextDetection.corners?.length || 0,
          detectorCorners: workerResult.detectorOutput?.corners || [],
          normalizedCorners: nextDetection.corners,
          confidence: roundMetric(nextDetection.confidence),
          widthRatio: roundMetric(nextDetection.widthRatio),
          heightRatio: roundMetric(nextDetection.heightRatio),
          angle: roundMetric(nextDetection.angle),
          perspectiveSkew: roundMetric(nextDetection.perspectiveSkew),
          positionValid: nextDetection.positionValid,
          nearPositionValid: nextDetection.nearPositionValid,
          sizeValid: nextDetection.sizeValid,
          nearSizeValid: nextDetection.nearSizeValid,
          angleValid:
            nextDetection.angleValid && nextDetection.perspectiveValid,
          nearAngleValid: nextDetection.nearAngleValid,
          nearPerspectiveValid: nextDetection.nearPerspectiveValid,
          complete: nextDetection.complete,
          recordingReady: nextDetection.recordingReady,
          valid: nextDetection.valid,
          fresh: nextDetection.fresh,
          missedFrames: nextDetection.missedFrames,
          stableFrames: nextDetection.stableFrames,
          stableProgress: roundMetric(nextDetection.stableProgress),
          detectorFps: roundMetric(nextDetection.detectorFps),
          detectorTimeMs: roundMetric(workerResult.detectorTimeMs),
        });
        debugState.lastStatus = nextDetection.status;
        debugState.lastLoggedAt = timestamp;
      }
      setDetection(nextDetection);
      setDetectionStatus(nextDetection.status);

      // 容错区间只用于自动模式补启动 3~8 秒滚动录像。
      // 自动模式通常在摄像头就绪时已预录；若录制意外未启动，这里也可补启动。
      // 是否允许拍照仍由下方 nextDetection.stable（严格合格且稳定 500ms）决定。
      const recordingShouldStart = nextDetection.recordingReady;
      if (
        nextDetection.fresh &&
        recordingShouldStart &&
        !recorderControllerRef.current?.isRecording()
      ) {
        const recordingStarted = startBackgroundRecording(
          streamRef.current,
          "document-positioned",
        );
        if (!recordingStarted) {
          handleCaptureError("kyc.camera.recordingFailed");
          return;
        }
      }

      const recordingElapsedMs = getRecordingElapsed(
        recordingStartedAtRef.current,
      );
      const recordingDurationReady = recordingElapsedMs >= MINIMUM_RECORDING_MS;
      const visibleDetectionStatus =
        nextDetection.stable && !recordingDurationReady
          ? "HOLD_STEADY"
          : nextDetection.status;

      if (
        automaticMode &&
        nextDetection.fresh &&
        nextDetection.stable &&
        recordingDurationReady
      ) {
        logDocumentCamera("Automatic capture conditions met", {
          side,
          stableDurationMs: nextDetection.stableElapsedMs,
          recordingElapsedMs,
          minimumRecordingMs: MINIMUM_RECORDING_MS,
        });
        transitionTo(STATUS.DOCUMENT_READY);
        captureAutomaticDocument();
        return;
      }

      setDetectionStatus(visibleDetectionStatus);

      transitionTo(
        nextDetection.detected ? STATUS.DOCUMENT_INVALID : STATUS.SCANNING,
      );
    };

    // 单帧失败先按“漏检”处理，让追踪器利用历史四角消除偶发抖动。
    const applyDetectionError = (detectionError, frameWidth, frameHeight) => {
      detectionErrorFramesRef.current += 1;
      const failedFrames = detectionErrorFramesRef.current;
      warnDocumentCamera("Detector frame failed", detectionError);

      applyDetection(
        {
          detectorOutput: {
            detected: false,
            corners: [],
            confidence: 0,
          },
          frameWidth,
          frameHeight,
          detectorTimeMs: 0,
        },
        performance.now(),
      );

      if (failedFrames > DOCUMENT_CONFIG.maxMissedFrames) {
        handleDetectionUnavailable(detectionError);
      }
    };

    // requestAnimationFrame 负责调度，但只按配置 FPS 取样；busy 锁保证同一时刻只有一个请求。
    const inspectFrame = (timestamp) => {
      if (stopped) return;

      const video = videoRef.current;
      const canvas = detectionCanvasRef.current;
      if (
        video?.readyState >= 2 &&
        video.videoWidth &&
        canvas &&
        !detectionBusyRef.current &&
        timestamp - lastDetectionAtRef.current >= detectionInterval
      ) {
        lastDetectionAtRef.current = timestamp;
        detectionBusyRef.current = true;
        const sampleHeight = Math.max(
          100,
          Math.round(
            DOCUMENT_CONFIG.detectorSampleWidth *
              (video.videoHeight / video.videoWidth),
          ),
        );
        canvas.width = DOCUMENT_CONFIG.detectorSampleWidth;
        canvas.height = sampleHeight;
        if (!videoMetadataLoggedRef.current) {
          videoMetadataLoggedRef.current = true;
          logDocumentCamera("Video frames are entering detector", {
            side,
            sourceWidth: video.videoWidth,
            sourceHeight: video.videoHeight,
            sampleWidth: DOCUMENT_CONFIG.detectorSampleWidth,
            sampleHeight,
          });
        }
        const context = canvas.getContext("2d", {
          willReadFrequently: true,
        });
        context.drawImage(
          video,
          0,
          0,
          DOCUMENT_CONFIG.detectorSampleWidth,
          sampleHeight,
        );
        const imageData = context.getImageData(
          0,
          0,
          DOCUMENT_CONFIG.detectorSampleWidth,
          sampleHeight,
        );

        detector
          .detect(imageData)
          .then((workerResult) => {
            if (!stopped) {
              detectionErrorFramesRef.current = 0;
              applyDetection(workerResult, performance.now());
            }
          })
          .catch((detectionError) => {
            if (!stopped) {
              applyDetectionError(
                detectionError,
                DOCUMENT_CONFIG.detectorSampleWidth,
                sampleHeight,
              );
            }
          })
          .finally(() => {
            detectionBusyRef.current = false;
          });
      }

      animationFrameRef.current =
        globalThis.requestAnimationFrame(inspectFrame);
    };

    animationFrameRef.current = globalThis.requestAnimationFrame(inspectFrame);
    return () => {
      logDocumentCamera("Document frame inspection stopped", { side });
      stopped = true;
      detectionBusyRef.current = false;
      globalThis.cancelAnimationFrame(animationFrameRef.current);
    };
  }, [
    captureAutomaticDocument,
    handleCaptureError,
    handleDetectionUnavailable,
    isDetecting,
    side,
    startBackgroundRecording,
    transitionTo,
  ]);

  // 模式切换会取消旧录像和追踪状态，但复用仍处于 live 的摄像头流。
  const selectMode = (nextMode) => {
    if (nextMode === mode || controlsLocked) return;

    logDocumentCamera("Capture mode changed", {
      side,
      from: mode,
      to: nextMode,
    });
    modeRef.current = nextMode;
    setIsDetecting(false);
    recorderControllerRef.current?.cancel();
    recorderControllerRef.current = null;
    recordingStartedAtRef.current = 0;
    setManualCountdown(0);
    resetDetection();
    setMode(nextMode);
    setError("");

    const streamIsLive = streamRef.current
      ?.getVideoTracks()
      .some((track) => track.readyState === "live");
    if (!streamIsLive) {
      transitionTo(STATUS.INITIAL);
      return;
    }

    transitionTo(STATUS.SCANNING);
    if (nextMode === "manual") {
      setDetectionStatus("MANUAL");
      return;
    }

    if (!startBackgroundRecording(streamRef.current, "automatic-mode")) {
      handleCaptureError("kyc.camera.recordingFailed");
      return;
    }

    getDocumentDetectionClient()
      .initialize()
      .then(() => {
        if (!mountedRef.current) return;
        logDocumentCamera("Detector is ready after mode change", {
          side,
          mode: nextMode,
        });
        if (
          mountedRef.current &&
          modeRef.current === nextMode &&
          statusRef.current === STATUS.SCANNING
        ) {
          setIsDetecting(true);
        }
      })
      .catch(handleDetectionUnavailable);
  };

  // 自动模式并行准备检测器与摄像头；手动模式只申请相机，不初始化或运行检测器。
  const startCamera = useCallback(async () => {
    logDocumentCamera("Requesting camera", {
      side,
      mode: modeRef.current,
      secureContext: globalThis.isSecureContext,
      hasGetUserMedia: Boolean(navigator.mediaDevices?.getUserMedia),
      hasMediaRecorder: Boolean(globalThis.MediaRecorder),
    });
    setError("");
    resetDetection();
    transitionTo(STATUS.REQUESTING);

    if (!navigator.mediaDevices?.getUserMedia || !globalThis.MediaRecorder) {
      handleCaptureError("kyc.camera.recordingUnsupported");
      return;
    }

    const automaticMode = modeRef.current === "automatic";
    const detectorReadyPromise = automaticMode
      ? getDocumentDetectionClient()
          .initialize()
          .then(() => {
            logDocumentCamera("Detector initialization confirmed", {
              side,
            });
            return { ready: true, error: null };
          })
          .catch((initializationError) => ({
            ready: false,
            error: initializationError,
          }))
      : null;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
          width: { ideal: 1280 },
          height: { ideal: 720 },
        },
        audio: false,
      });

      if (!mountedRef.current) {
        stream.getTracks().forEach((track) => track.stop());
        return;
      }

      streamRef.current = stream;
      const videoTrack = stream.getVideoTracks()[0];
      const trackSettings = videoTrack?.getSettings?.() || {};
      logDocumentCamera("Camera stream active", {
        side,
        camera: videoTrack?.label || "Unnamed camera",
        width: trackSettings.width,
        height: trackSettings.height,
        frameRate: trackSettings.frameRate,
      });

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        resetDetection();
      }

      if (
        modeRef.current === "automatic" &&
        !startBackgroundRecording(stream, "camera-ready")
      ) {
        handleCaptureError("kyc.camera.recordingFailed");
        return;
      }

      transitionTo(STATUS.SCANNING);

      if (!automaticMode) {
        setDetectionStatus("MANUAL");
        return;
      }

      const detectorInitialization = await detectorReadyPromise;
      if (!detectorInitialization.ready) {
        handleDetectionUnavailable(detectorInitialization.error);
        return;
      }

      if (!mountedRef.current || statusRef.current === STATUS.ERROR) return;
      logDocumentCamera("Document detection enabled", {
        side,
        mode: modeRef.current,
      });
      setIsDetecting(true);
    } catch (cameraError) {
      warnDocumentCamera("Camera request failed", cameraError);
      const denied = ["NotAllowedError", "SecurityError"].includes(
        cameraError?.name,
      );
      handleCaptureError(
        denied ? "kyc.camera.permissionDenied" : "kyc.camera.unavailable",
      );
    }
  }, [
    handleCaptureError,
    handleDetectionUnavailable,
    resetDetection,
    side,
    startBackgroundRecording,
    transitionTo,
  ]);

  // 页面默认自动进入相机；模式变化后 effect 可按当前状态安全地重新判断。
  useEffect(() => {
    if (!autoStart || statusRef.current !== STATUS.INITIAL) {
      return;
    }

    startCamera();
  }, [autoStart, mode, startCamera]);

  const viewportStatus =
    cameraStatus === STATUS.PROCESSING
      ? "PROCESSING"
      : cameraStatus === STATUS.RECORDING
        ? "RECORDING"
        : detectionStatus;

  return (
    <div className="document-camera mx-auto w-full max-w-lg">
      {/* 模式切换：自动模式自行触发，手动模式等待用户点击。 */}
      <div
        className="document-camera-tabs relative z-10 mx-auto grid w-fit grid-cols-2 rounded-full bg-slate-100/90 p-[2px] shadow-[0_4px_12px_rgba(15,23,42,0.10)] ring-1 ring-slate-200/80 backdrop-blur-sm"
        role="tablist"
        aria-label={t("kyc.camera.captureMode")}
      >
        {["automatic", "manual"].map((item) => (
          <button
            key={item}
            type="button"
            role="tab"
            aria-selected={mode === item}
            disabled={controlsLocked}
            onClick={() => selectMode(item)}
            className={`min-h-7 min-w-[4.25rem] rounded-full px-2.5 text-xs font-semibold transition-all disabled:cursor-not-allowed disabled:opacity-50 ${
              mode === item
                ? "bg-white text-slate-950 shadow-[0_2px_7px_rgba(15,23,42,0.18)] ring-1 ring-white"
                : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {t(`kyc.camera.mode.${item}`)}
          </button>
        ))}
      </div>

      {/* 实时视频层：video -> 四角 overlay -> 状态/debug -> 拍照闪光。 */}
      <div className="document-camera-viewport relative mt-2.5 aspect-[4/3] overflow-hidden rounded-[1.25rem] bg-[#111827] shadow-[0_12px_30px_rgba(15,23,42,0.14)] ring-1 ring-slate-900/10">
        <video
          ref={videoRef}
          muted
          playsInline
          className={`size-full object-cover ${
            cameraIsLive ? "block" : "invisible"
          }`}
        />

        {!cameraIsLive && (
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_45%,rgba(45,212,191,0.08),transparent_48%),#0b1220]" />
        )}

        {!cameraIsLive && (
          <DetectionOverlay
            detection={detection}
            status="SEARCHING"
            objectFit="cover"
          />
        )}

        {cameraIsLive && mode === "automatic" && (
          <>
            <DetectionOverlay
              detection={detection}
              status={viewportStatus}
              objectFit="cover"
            />
            <DocumentDetectionDebug detection={detection} />
          </>
        )}

        {cameraIsLive && mode === "manual" && (
          <DetectionOverlay
            detection={detection}
            status="MANUAL"
            objectFit="cover"
          />
        )}

        {cameraIsLive &&
          cameraStatus !== STATUS.PROCESSING &&
          cameraStatus !== STATUS.RECORDING && (
            <DetectionStatus
              status={mode === "manual" ? "GOOD" : viewportStatus}
            />
          )}

        {mode === "manual" && cameraStatus === STATUS.RECORDING && (
          <div
            className="absolute inset-0 z-20 flex items-center justify-center bg-slate-950/20 px-4"
            role="timer"
            aria-live="polite"
            aria-atomic="true"
            aria-label={t("kyc.camera.manualRecordingAnnouncement", {
              count: manualCountdown,
            })}
          >
            <div className="flex flex-col items-center" aria-hidden="true">
              <div className="manual-capture-countdown relative flex size-[3.25rem] items-center justify-center rounded-full bg-slate-950/40 shadow-[0_6px_18px_rgba(2,6,23,0.26)] backdrop-blur-sm">
                <svg
                  className="absolute inset-0 size-full -rotate-90"
                  viewBox="0 0 60 60"
                  aria-hidden="true"
                >
                  <circle
                    cx="30"
                    cy="30"
                    r="25"
                    fill="none"
                    stroke="rgba(255,255,255,0.22)"
                    strokeWidth="2.5"
                  />
                  <circle
                    className="manual-capture-countdown-ring"
                    cx="30"
                    cy="30"
                    r="25"
                    fill="none"
                    pathLength="100"
                    stroke="#5eead4"
                    strokeWidth="2.5"
                    strokeLinecap="round"
                    strokeDasharray="100"
                    style={{ strokeDashoffset: 100 - manualProgress * 100 }}
                  />
                </svg>
                <span className="relative text-lg font-semibold tabular-nums leading-none text-white">
                  {manualCountdown}
                </span>
              </div>
              <p className="mt-2 inline-flex items-center gap-1.5 rounded-full border border-white/10 bg-slate-950/65 px-2.5 py-1 text-xs font-medium text-white shadow-md backdrop-blur-sm">
                <span className="size-1 rounded-full bg-teal-300 shadow-[0_0_6px_rgba(94,234,212,0.85)]" />
                {t("kyc.camera.manualRecordingTitle")}
              </p>
            </div>
          </div>
        )}

        {showFlash && (
          <span className="document-capture-flash pointer-events-none absolute inset-0 z-30 bg-white" />
        )}
      </div>

      {/* 业务说明和手动快门；手动模式相机就绪后即可开始固定 3 秒录像。 */}
      <div className="document-camera-copy mt-3.5 text-center">
        <h1
          id="kyc-document-capture-title"
          className="text-lg font-semibold tracking-[-0.02em] text-slate-950 sm:text-xl"
        >
          {t(
            isBack
              ? "kyc.camera.backTitle"
              : isPassport
                ? "kyc.camera.passportTitle"
                : "kyc.camera.frontTitle",
          )}
        </h1>
        <p className="mx-auto mt-2 max-w-md text-sm leading-6 text-slate-500">
          {t(
            mode === "manual"
              ? "kyc.camera.manualDescription"
              : "kyc.camera.automaticDescription",
          )}
        </p>
      </div>

      <div className="document-camera-actions mx-auto mt-3 max-w-md">
        {mode === "manual" && (
          <button
            type="button"
            className={`document-camera-shutter mx-auto flex size-11 items-center justify-center rounded-full border-2 border-slate-300 bg-white p-1.5 shadow-[0_6px_18px_rgba(15,23,42,0.16)] transition active:scale-95 disabled:cursor-not-allowed disabled:opacity-50 ${
              [STATUS.RECORDING, STATUS.PROCESSING].includes(cameraStatus)
                ? "animate-pulse"
                : ""
            }`}
            onClick={startManualCapture}
            disabled={!cameraIsLive || controlsLocked}
            aria-label={t("kyc.camera.startManualCapture")}
            title={t("kyc.camera.startManualCapture")}
          >
            <span className="size-full rounded-full bg-teal-700 shadow-inner" />
          </button>
        )}
      </div>

      <button
        type="button"
        onClick={() => setShowHelp((current) => !current)}
        className="document-camera-help mx-auto mt-2 flex min-h-8 items-center justify-center gap-1.5 rounded-full px-3 text-sm font-medium text-slate-500 transition hover:bg-slate-50 hover:text-slate-950 disabled:cursor-not-allowed disabled:opacity-50"
        disabled={controlsLocked}
        aria-expanded={showHelp}
      >
        <HelpCircle className="size-4" aria-hidden="true" />
        {t("kyc.camera.havingTrouble")}
      </button>

      {showHelp && (
        <div className="mx-auto mt-2 max-w-lg rounded-xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm leading-6 text-slate-600">
          {t("kyc.camera.helpText")}
        </div>
      )}

      {error && (
        <p
          className="mt-4 flex items-start gap-2 rounded-xl border border-red-100 bg-red-50 px-4 py-3 text-sm leading-5 text-red-700"
          role="alert"
        >
          <CircleAlert className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
          {error}
        </p>
      )}

      <canvas ref={detectionCanvasRef} className="hidden" />
      <canvas ref={previewCanvasRef} className="hidden" />
    </div>
  );
}
